import { useCallback } from 'react';

import { SourceUnloadedEvent } from 'bitmovin-player-react-native';
import {
    BufferType,
    DownloadFinishedEvent,
    MediaType,
    PlayerAPI,
    PlayerEvent,
    PlayerEventCallback,
    VideoDownloadQualityChangedEvent,
} from 'bitmovin-player/modules/bitmovinplayer-core';

import debugLog from 'utils/debugLog.ts';

import useEventHandler from 'components/Player/web/useEventHandler.ts';

import { PlayerProps } from '../../types.ts';
import useStatisticsUpdateInterval, {
    initialStatisticsState,
} from './useStatisticsUpdateInterval.ts';

function useStatisticsUpdate(
    player: PlayerAPI | null,
    playing: boolean,
    onStatisticsUpdate?: PlayerProps['onStatisticsUpdate'],
    statisticsUpdateInterval?: number,
) {
    const { downloadBitrateAverageAttempts, setStatistics } =
        useStatisticsUpdateInterval(
            playing,
            onStatisticsUpdate,
            statisticsUpdateInterval,
        );

    const onVideoDownloadQualityChanged = useCallback(
        (e: VideoDownloadQualityChangedEvent) => {
            downloadBitrateAverageAttempts.current += 1;
            setStatistics((prevState) => ({
                ...prevState,
                downloadBitrate: e.targetQuality?.bitrate,
                downloadBitrateAverage:
                    (prevState.downloadBitrateAverage || 0) +
                    (e.targetQuality?.bitrate || 0) /
                        downloadBitrateAverageAttempts.current,
            }));
        },
        [player],
    );

    const onDownloadFinished = useCallback(
        (e: DownloadFinishedEvent) => {
            const downloadedVideoData = player?.getDownloadedVideoData();

            setStatistics((prevState) => ({
                ...prevState,
                url: player?.getSource()?.hls || player?.getSource()?.dash,
                availableBitrates: player
                    ?.getAvailableVideoQualities()
                    ?.map((quality) => quality.bitrate),
                streamBitrate: player?.getVideoQuality().bitrate,
                selectedBitrate: downloadedVideoData?.bitrate,
                bufferedDuration:
                    player?.buffer?.getLevel(
                        BufferType.ForwardDuration,
                        MediaType.Video,
                    )?.level ?? undefined,
                totalVideoFrames: player?.getVideoQuality().frameRate || 0,
                droppedVideoFrames: player?.getDroppedVideoFrames(),
                bytesDownloaded: (prevState.bytesDownloaded || 0) + e.size,
            }));
        },
        [player],
    );

    const onSourceUnloaded = useCallback((e: SourceUnloadedEvent) => {
        debugLog('onSourceUnloaded:', e);
        downloadBitrateAverageAttempts.current = 0;
        setStatistics(initialStatisticsState);
    }, []);

    useEventHandler(
        player,
        PlayerEvent.VideoDownloadQualityChanged,
        onVideoDownloadQualityChanged as PlayerEventCallback,
    );
    useEventHandler(
        player,
        PlayerEvent.DownloadFinished,
        onDownloadFinished as PlayerEventCallback,
    );
    useEventHandler(
        player,
        PlayerEvent.SourceUnloaded,
        onSourceUnloaded as unknown as PlayerEventCallback,
    );
}

export default useStatisticsUpdate;
